import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { modifier } from 'ember-modifier';
import { htmlSafe } from '@ember/template';
import { guidFor } from '@ember/object/internals';
import { t } from 'ember-intl';
import { PhCaretDown } from 'ember-phosphor-icons';
import FormField from './form-field';
import {
  computeDropdownPosition,
  installEscapeHandler,
  installOutsideClickHandler,
  installScrollHandler,
} from '../utils/floating';
import type IntlService from 'ember-intl/services/intl';

// ---------------------------------------------------------------------------
// PhoneInput – national-format entry, E.164 output
// ---------------------------------------------------------------------------

export type PhoneInputSize = 'sm' | 'md' | 'lg';

/** The regions the field can read. `US` is every `+1` number as far as the field can tell —
 * the US and Canada share the dial code and nothing here splits them; the server refuses the
 * NANP countries outside the launch by area code. Deliberately closed: without numbering-plan
 * metadata the component cannot honestly validate elsewhere. */
export type PhoneCountryCode = 'US' | 'MX';

/** One entry in the country selector. */
export interface PhoneCountry {
  iso: PhoneCountryCode;
  dialCode: string;
  label: string;
}

/**
 * Everything the field knows about its number, reported as one object so no caller
 * ever has to reconcile a number with a region reported separately.
 */
export interface PhoneValue {
  /** What the field displays: NANP grouping, bare digits for Mexico, empty when empty. */
  national: string;
  /** Set only while `national` is a complete number that could be dialled under `country`. */
  e164: string | null;
  /** The region the number is read under: a dial code the member typed or the caller
   * stored, else the member's selection, else the caller's default. */
  country: PhoneCountryCode;
}

const COUNTRY_CONFIG = {
  US: { dialCode: '1' },
  MX: { dialCode: '52' },
} as const satisfies Record<PhoneCountryCode, { dialCode: string }>;

/** Every region the field can read, in selector order. */
export const ALL_REGIONS: readonly PhoneCountryCode[] = ['US', 'MX'];

/** The region a value is read under when neither the caller nor the member names one. */
export const DEFAULT_COUNTRY: PhoneCountryCode = 'US';

function countryForInternationalInput(
  digits: string,
  regions: readonly PhoneCountryCode[]
): PhoneCountryCode | undefined {
  return regions.find((region) => digits.startsWith(COUNTRY_CONFIG[region].dialCode));
}

function nationalDigits(
  value: string,
  fallback: PhoneCountryCode,
  regions: readonly PhoneCountryCode[]
) {
  const raw = value.trim();
  const digits = raw.replace(/\D/g, '');

  if (!digits) return { country: fallback, national: '' };

  if (raw.startsWith('+')) {
    const country = countryForInternationalInput(digits, regions);
    if (!country) return undefined;

    return { country, national: digits.slice(COUNTRY_CONFIG[country].dialCode.length) };
  }

  const national =
    fallback !== 'MX' && digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  return { country: fallback, national };
}

/** NANP structure, which needs no metadata: an area code and an exchange both start
 * 2-9, and an N11 area code is a service code rather than a subscriber. Without this
 * the field goes green on numbers the server's validator rejects, and enrollment then
 * discards them — the member is told nothing at the one moment they could correct it. */
function isNanpShaped(national: string): boolean {
  const area = national.slice(0, 3);

  return /^[2-9]/.test(area) && /^[2-9]/.test(national.slice(3, 6)) && !/^\d11$/.test(area);
}

function toE164(
  value: string,
  fallback: PhoneCountryCode,
  regions: readonly PhoneCountryCode[]
): string | null {
  const parsed = nationalDigits(value, fallback, regions);
  if (!parsed || parsed.national.length !== 10) return null;
  if (parsed.country !== 'MX' && !isNanpShaped(parsed.national)) return null;

  return `+${COUNTRY_CONFIG[parsed.country].dialCode}${parsed.national}`;
}

/** Grouping only where it is truthful: NANP is (AAA) EEE-LLLL everywhere, while
 * Mexican local-area-code lengths vary, so those digits are left ungrouped rather
 * than forced into a shape that is wrong for most of the country. */
function formatNational(
  value: string,
  fallback: PhoneCountryCode,
  regions: readonly PhoneCountryCode[]
): string {
  const raw = value.trim();
  const parsed = nationalDigits(raw, fallback, regions);
  if (!parsed) return value;

  const { country, national } = parsed;
  // A pasted E.164 stays intact until it holds a complete supported number.
  if (raw.startsWith('+') && national.length < 10) return value;
  if (country === 'MX') return national;

  if (national.length <= 3) return national;
  if (national.length <= 6) return `(${national.slice(0, 3)}) ${national.slice(3)}`;

  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6, 10)}`;
}

/**
 * The one way a string becomes a `PhoneValue` — the field uses it for every keystroke,
 * and a surface uses it for a number it holds before the field has reported anything.
 *
 * @param value a typed, pasted or stored number; a `+` prefix names its own region
 * @param fallback the region for a value without a dial code; a caller keeps it within `regions`
 * @param regions the dial codes recognised — one outside the list is left as typed and
 *   undialable; defaults to every region, which is right for a stored number
 * @returns the display text, its E.164 when dialable, and the region it was read under
 */
export function readPhone(
  value: string,
  fallback: PhoneCountryCode,
  regions: readonly PhoneCountryCode[] = ALL_REGIONS
): PhoneValue {
  const raw = value.trim();
  const digits = raw.replace(/\D/g, '');
  // A lone `+` is the start of a dial code, not nothing; anything else without a digit is.
  if (!digits) return { national: raw.startsWith('+') ? '+' : '', e164: null, country: fallback };

  const dialled = raw.startsWith('+') ? countryForInternationalInput(digits, regions) : undefined;
  const country = dialled ?? fallback;
  const national = formatNational(raw, country, regions);

  // From the display, not the entry: the field drops digits past a NANP number, so what
  // it shows is what it dials.
  return { national, e164: toE164(national, country, regions), country };
}

/** An empty field under `country`. */
export function emptyPhone(country: PhoneCountryCode = DEFAULT_COUNTRY): PhoneValue {
  return { national: '', e164: null, country };
}

interface PhoneInputSignature {
  Args: {
    /** Emitted as `data-test-hook`; see `Input` for why this is not the DOM id. */
    testHook?: string;
    /** Controlled: bind this to what the last `@onChange` reported, or to `readPhone`
     * of a stored number. Omitted means empty, read under `@defaultCountry`. */
    value?: PhoneValue;
    /** Fires on every keystroke and every region pick with the whole value, so a
     * submit before blur has the same number and region a blur would. */
    onChange?: (value: PhoneValue) => void;
    /** Forwarded on blur so the `<Form>` orchestrator can mark the field touched. */
    onBlur?: () => void;
    /** Region of an empty field, until the member types a dial code or picks one @default "US" */
    defaultCountry?: PhoneCountryCode;
    /** Regions on offer. One region renders a plain field with its dial code as a prefix,
     * and a typed dial code outside the list is not a number. @default every region */
    countries?: PhoneCountryCode[];
    /** Caller-supplied errors; these replace the built-in invalid message. */
    errors?: string[];
    hint?: string;
    label?: string;
    /** Renders the muted "Optional" qualifier inside the label row. Qualifies
     * `@label`, so it has no effect without one — a surface that renders its own
     * heading instead should carry the wording there. */
    isOptional?: boolean;
    isRequired?: boolean;
    isDisabled?: boolean;
    /** @default "md" — every size still clears a 44px tap target. */
    size?: PhoneInputSize;
    placeholder?: string;
    name?: string;
  };
  Element: HTMLDivElement;
}

/**
 * A phone field that displays national format and reports E.164, for the NANP and
 * Mexico only — without numbering-plan metadata it cannot honestly go further.
 * Controlled on a single `PhoneValue`: the field holds no region of its own, so what
 * it displays, what it validates and what the caller submits can never disagree.
 *
 * @example
 * ```gts
 * <PhoneInput @label="Mobile phone" @value={{this.phone}} @onChange={{this.setPhone}} />
 * ```
 */
export default class PhoneInput extends Component<PhoneInputSignature> {
  @service declare intl: IntlService;

  private fieldId = `tio-phone-input-${guidFor(this)}`;
  private triggerId = `${this.fieldId}-country`;
  private prefixId = `${this.fieldId}-prefix`;

  @tracked private touched = false;
  @tracked private isOpen = false;
  @tracked private triggerRect?: DOMRect;
  @tracked private activeIndex = 0;

  private triggerElement?: HTMLElement;
  private optionElements = new Map<number, HTMLElement>();

  // ---- Derived state --------------------------------------------------------

  private get size(): PhoneInputSize {
    return this.args.size ?? 'md';
  }

  private get regions(): readonly PhoneCountryCode[] {
    return this.args.countries?.length ? this.args.countries : ALL_REGIONS;
  }

  private get hasSelector(): boolean {
    return this.regions.length > 1;
  }

  /** The caller's default when it is on offer, else the first region that is. */
  private get defaultRegion(): PhoneCountryCode {
    const preferred = this.args.defaultCountry ?? DEFAULT_COUNTRY;

    return this.regions.includes(preferred) ? preferred : (this.regions[0] ?? DEFAULT_COUNTRY);
  }

  private get value(): PhoneValue {
    return this.args.value ?? emptyPhone(this.defaultRegion);
  }

  private get country(): PhoneCountryCode {
    return this.value.country;
  }

  /** A stored number may sit in a region no longer on offer; a keystroke on it is read
   * under one that is. */
  private get typingRegion(): PhoneCountryCode {
    return this.regions.includes(this.country) ? this.country : this.defaultRegion;
  }

  private get countries(): PhoneCountry[] {
    return this.regions.map((iso) => ({
      iso,
      dialCode: this.dialCode(iso),
      label: this.regionName(iso),
    }));
  }

  private dialCode(iso: PhoneCountryCode): string {
    return `+${COUNTRY_CONFIG[iso].dialCode}`;
  }

  /** Falls back to the value's own region rather than the first entry, so a stored
   * number in a region no longer on offer is not mislabelled. */
  private get selected(): PhoneCountry {
    const listed = this.countries.find((c) => c.iso === this.country);
    if (listed) return listed;

    return {
      iso: this.country,
      dialCode: this.dialCode(this.country),
      label: this.regionName(this.country),
    };
  }

  /** Re-grouped on the way out, so a value a caller assembled by hand still reads right. */
  private get displayValue(): string {
    return formatNational(this.value.national, this.country, this.regions);
  }

  private get isEmpty(): boolean {
    return this.displayValue.trim().length === 0;
  }

  /** Empty is valid — the field is optional on every surface that uses it. */
  private get isInvalid(): boolean {
    if (!this.touched || this.isEmpty) return false;

    return toE164(this.displayValue, this.country, this.regions) === null;
  }

  private get errors(): string[] | undefined {
    if (this.args.errors?.length) return this.args.errors;

    return this.isInvalid ? [this.intl.t('common.phone_input.invalid')] : undefined;
  }

  /** The hint, plus the dial-code prefix when it is static text the input's name omits. */
  private get describedBy(): string | undefined {
    const ids = [
      this.args.hint ? `${this.fieldId}-hint` : null,
      this.hasSelector ? null : this.prefixId,
    ];
    const present = ids.filter((id): id is string => id !== null);

    return present.length ? present.join(' ') : undefined;
  }

  private get inputClasses(): string {
    return `app-phone-field app-input--${this.size}`;
  }

  /** The error state lives on the row, not the inner input: the shared
   * .app-input--has-error rule carries its own focus ring, which would render a
   * second square-cornered ring inside the row's rounded one. */
  private get rowClasses(): string {
    return this.errors?.length ? 'app-phone-row app-phone-row--has-error' : 'app-phone-row';
  }

  private get dropdownStyle() {
    if (!this.triggerRect) return htmlSafe('display:none;');

    const pos = computeDropdownPosition(this.triggerRect, { maxHeight: 260, offset: 4 });

    return htmlSafe(
      `position: fixed; top: ${pos.top}px; left: ${pos.left}px; min-width: ${Math.max(pos.width, 220)}px;` +
        ` max-height: 260px; transform-origin: ${pos.transformOrigin};` +
        ` transform: ${pos.openAbove ? 'translateY(-100%)' : 'none'}; z-index: 90;`
    );
  }

  private regionName(iso: PhoneCountryCode): string {
    return this.intl.t(`common.phone_input.regions.${iso}`);
  }

  // ---- Event handlers -------------------------------------------------------

  handleInput = (event: Event) => {
    this.args.onChange?.(
      readPhone((event.target as HTMLInputElement).value, this.typingRegion, this.regions)
    );
  };

  handleBlur = () => {
    this.touched = true;
    this.args.onBlur?.();
  };

  // ---- Country selector -----------------------------------------------------

  toggle = () => {
    if (this.args.isDisabled) return;

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  private open() {
    // Measured here rather than in a modifier: the rect has to be sampled at the
    // moment of opening, and a modifier runs on insert — by which time the trigger
    // may have moved. Select measures at the same point for the same reason.
    this.triggerRect = this.triggerElement?.getBoundingClientRect();
    this.activeIndex = Math.max(
      this.countries.findIndex((c) => c.iso === this.country),
      0
    );
    this.isOpen = true;
  }

  private close(refocus = true) {
    this.isOpen = false;
    if (refocus) this.triggerElement?.focus();
  }

  selectCountry = (iso: PhoneCountryCode) => {
    const { national, country } = this.value;
    this.close();

    // A number cannot move between dial codes, so a pick across one starts the field
    // over rather than re-reading Mexican digits as a NANP number.
    const crossesDialCode =
      !!national && COUNTRY_CONFIG[country].dialCode !== COUNTRY_CONFIG[iso].dialCode;

    this.args.onChange?.(
      crossesDialCode ? emptyPhone(iso) : readPhone(national, iso, this.regions)
    );
  };

  handleTriggerKeydown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isOpen) this.open();
    }
  };

  handleListKeydown = (event: KeyboardEvent) => {
    const last = this.countries.length - 1;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.focusOption(Math.min(this.activeIndex + 1, last));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusOption(Math.max(this.activeIndex - 1, 0));
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.focusOption(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.focusOption(last);
    } else if (event.key === 'Tab') {
      this.close(false);
    }
  };

  private focusOption(index: number) {
    this.activeIndex = index;
    this.optionElements.get(index)?.focus();
  }

  registerTrigger = modifier((element: HTMLElement) => {
    this.triggerElement = element;

    return () => {
      this.triggerElement = undefined;
    };
  });

  registerOption = modifier((element: HTMLElement, [index]: [number]) => {
    this.optionElements.set(index, element);
    if (index === this.activeIndex) element.focus();

    return () => this.optionElements.delete(index);
  });

  registerListbox = modifier((element: HTMLElement) => {
    const cleanupOutside = installOutsideClickHandler([this.triggerElement ?? null, element], () =>
      this.close(false)
    );
    const cleanupEscape = installEscapeHandler(() => this.close(), this.triggerElement);
    // position:fixed, so a scrolling modal would otherwise leave the list floating away
    // from its trigger. Matches Select's treatment.
    const cleanupScroll = installScrollHandler({
      floatingEl: element,
      mode: 'close',
      onClose: () => this.close(false),
      triggerEl: this.triggerElement,
      debounceMs: 150,
    });

    return () => {
      cleanupOutside();
      cleanupEscape();
      cleanupScroll();
    };
  });

  private get portalDestination(): Element {
    return document.body;
  }

  <template>
    <FormField
      @label={{@label}}
      @hint={{@hint}}
      @errors={{this.errors}}
      @isRequired={{@isRequired}}
      @isDisabled={{@isDisabled}}
      @size={{this.size}}
      @fieldId={{this.fieldId}}
      data-test-phone-input-root
      data-test-hook={{@testHook}}
      ...attributes
    >
      {{! Inside the label row, so it reads as a qualifier and not as a second line. }}
      <:labelSuffix>
        {{#if @isOptional}}
          <span data-test-phone-input-optional class="tio-text-xs-medium app-phone-optional">
            {{t "common.phone_input.optional"}}
          </span>
        {{/if}}
      </:labelSuffix>

      <:default as |field|>
        <div class={{this.rowClasses}}>
          {{#if this.hasSelector}}
            <button
              data-test-phone-input-country
              id={{this.triggerId}}
              type="button"
              class="app-phone-country"
              disabled={{@isDisabled}}
              aria-haspopup="listbox"
              aria-expanded={{if this.isOpen "true" "false"}}
              aria-label={{t
                "common.phone_input.country_selected"
                country=this.selected.label
                dialCode=this.selected.dialCode
              }}
              {{this.registerTrigger}}
              {{on "click" this.toggle}}
              {{on "keydown" this.handleTriggerKeydown}}
            >
              <span aria-hidden="true">{{this.selected.iso}}</span>
              <span class="tio-fg-muted" aria-hidden="true">{{this.selected.dialCode}}</span>
              <PhCaretDown @size={{12}} aria-hidden="true" />
            </button>
          {{else}}
            <span
              data-test-phone-input-prefix
              id={{this.prefixId}}
              class="app-phone-country app-phone-country--static tio-fg-muted"
            >{{this.selected.dialCode}}</span>
          {{/if}}

          {{! template-lint-disable require-input-label }}
          {{! The two labelling paths are mutually exclusive at runtime: aria-label is
          emitted only when @label is absent, in which case FormField renders no
          <label for> at all. The rule cannot see the condition. }}
          <input
            data-test-phone-input-field
            id={{field.fieldId}}
            type="tel"
            inputmode="tel"
            autocomplete="tel"
            class={{this.inputClasses}}
            value={{this.displayValue}}
            name={{@name}}
            placeholder={{@placeholder}}
            disabled={{@isDisabled}}
            required={{@isRequired}}
            aria-label={{unless @label (t "common.phone_input.label_fallback")}}
            aria-invalid={{if field.hasErrors "true"}}
            aria-describedby={{this.describedBy}}
            {{on "input" this.handleInput}}
            {{on "blur" this.handleBlur}}
          />
        </div>

        {{#if this.isOpen}}
          {{#in-element this.portalDestination insertBefore=null}}
            <div
              data-test-phone-input-listbox
              data-floating-trigger-id={{this.triggerId}}
              role="listbox"
              aria-labelledby={{this.triggerId}}
              class="app-popover app-popover-body"
              style={{this.dropdownStyle}}
              {{this.registerListbox}}
            >
              {{#each this.countries key="iso" as |item index|}}
                <button
                  data-test-phone-input-option={{item.iso}}
                  type="button"
                  role="option"
                  aria-selected={{if (eq item.iso this.country) "true" "false"}}
                  class="app-popover-option app-popover-option--{{this.size}}
                    {{if (eq index this.activeIndex) 'app-popover-option--highlighted'}}
                    {{if (eq item.iso this.country) 'app-popover-option--selected'}}"
                  {{this.registerOption index}}
                  {{on "click" (fn this.selectCountry item.iso)}}
                  {{on "keydown" this.handleListKeydown}}
                >
                  <span>{{item.label}}</span>
                  <span class="tio-fg-muted">{{item.dialCode}}</span>
                </button>
              {{/each}}
            </div>
          {{/in-element}}
        {{/if}}
      </:default>
    </FormField>
  </template>
}

export type { PhoneInputSignature };
